import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSkillInference, type SkillInferenceUploadStatus } from '@/hooks/skills/useSkillInference.tsx';
import { toast } from '@/lib/toast';
import { useSheetVisibility } from '@/contexts/SheetVisibilityContext/useSheetVisibility';

const SHEET_ROUTE = '/skills/add/upload-document';
const REVIEW_ROUTE = '/skills/review-inferred-skills';
const COMPLETION_TOAST_ID = 'skill-inference-complete';
const EMPTY_TOAST_ID = 'skill-inference-empty';
const FAILURE_TOAST_ID = 'skill-inference-failed';

/**
 * Surfaces the outcome of a background document upload as a toast. Mounted once in `Layout`, since
 * the upload keeps running after the user navigates away from the upload sheet.
 */
export const useSkillInferenceToasts = (): void => {
    const navigate = useNavigate();
    const { uploadStatus, skills } = useSkillInference();
    const { isSheetOpen } = useSheetVisibility();
    const previousStatusRef = useRef<SkillInferenceUploadStatus | null>(null);

    useEffect(() => {
        const previous = previousStatusRef.current;
        previousStatusRef.current = uploadStatus;

        // The sheet already shows this outcome inline so a toast on top of it would be redundant.
        if (isSheetOpen) return;

        if (previous === 'processing' && uploadStatus === 'reviewing' && skills.length > 0) {
            toast.success('Your skills are ready!', {
                id: COMPLETION_TOAST_ID,
                description: 'Thanks for waiting. Now check out your results.',
                action: {
                    label: 'See skills',
                    onClick: () => navigate(REVIEW_ROUTE),
                },
            });
        }

        if (previous === 'processing' && uploadStatus === 'reviewing' && skills.length === 0) {
            toast.error('No skills found', {
                id: EMPTY_TOAST_ID,
                description: 'Some skills from this file might already be present in your Skills Portfolio.',
                action: {
                    label: 'Try again',
                    onClick: () => navigate(SHEET_ROUTE),
                },
            });
        }

        if (previous === 'processing' && uploadStatus === 'failed') {
            toast.error('Upload failed', {
                id: FAILURE_TOAST_ID,
                description: 'Something went wrong!',
                action: {
                    label: 'Try again',
                    onClick: () => navigate(SHEET_ROUTE),
                },
            });
        }
    }, [uploadStatus, skills.length, navigate, isSheetOpen]);
};
